import React, { useEffect, useState, useMemo, useCallback } from "react";
import DataTable from "react-data-table-component";
import Header from "./Header";
import Footer from "./Footer";
import API_BASE_URL from "../Config";
import "./styles/Medicinetype.css";
import "../styles/global.css";

/* ────────── SVG Icon System ────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block", flexShrink: 0 }}
  >
    {d}
  </svg>
);

const SearchIcon      = (p) => <Icon {...p} d={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} />;
const PlusIcon        = (p) => <Icon {...p} d={<><path d="M12 5v14M5 12h14" /></>} />;
const EditIcon        = (p) => <Icon {...p} d={<><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" /></>} />;
const TrashIcon       = (p) => <Icon {...p} d={<><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6 18 20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /></>} />;
const CloseIcon       = (p) => <Icon {...p} d={<><path d="M18 6 6 18M6 6l12 12" /></>} />;
const PillIcon        = (p) => <Icon {...p} d={<><path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" /><path d="m8.5 8.5 7 7" /></>} />;
const FilterIcon      = (p) => <Icon {...p} d={<><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></>} />;
const ChevronDownIcon = (p) => <Icon {...p} d={<><path d="m6 9 6 6 6-6" /></>} />;
const ChevronLeftIcon = (p) => <Icon {...p} d={<><path d="m15 18-6-6 6-6" /></>} />;
const ChevronRightIcon= (p) => <Icon {...p} d={<><path d="m9 18 6-6-6-6" /></>} />;
const CheckIcon       = (p) => <Icon {...p} d={<><path d="M20 6 9 17l-5-5" /></>} />;
const InfoIcon        = (p) => <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>} />;

/* ────────── iOS Toggle Switch ────────── */
const IosSwitch = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={onChange}
    className={`ios-switch ${checked ? "is-on" : "is-off"}`}
    title={checked ? "Active — click to deactivate" : "Inactive — click to activate"}
  >
    <span className="ios-switch__thumb" />
  </button>
);

/* ────────── Confirm Dialog ────────── */
const ConfirmDialog = ({ open, title, message, confirmLabel = "Confirm", confirmVariant = "danger", onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="cdlg-overlay" role="dialog" aria-modal="true" aria-labelledby="cdlg-title">
      <div className="cdlg-box">
        <div className={`cdlg-icon-wrap cdlg-icon-${confirmVariant}`}>
          {confirmVariant === "danger" ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
        </div>
        <h3 id="cdlg-title" className="cdlg-title">{title}</h3>
        <p className="cdlg-message">{message}</p>
        <div className="cdlg-actions">
          <button type="button" className="cdlg-btn cdlg-btn-cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className={`cdlg-btn cdlg-btn-confirm cdlg-btn-${confirmVariant}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ────────── Multi-Select Dropdown ────────── */
const MultiSelect = ({ options, selected, onChange, placeholder = "Filter by status…" }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter((s) => s !== val));
    else onChange([...selected, val]);
  };

  const label = selected.length === 0
    ? placeholder
    : selected.length === 1
    ? selected[0]
    : `${selected.length} selected`;

  return (
    <div className="mt-multiselect" ref={ref}>
      <button type="button" className={`mt-ms-trigger ${open ? "open" : ""}`} onClick={() => setOpen((o) => !o)}>
        <FilterIcon size={15} />
        <span>{label}</span>
        {selected.length > 0 && <span className="ms-count">{selected.length}</span>}
        <ChevronDownIcon size={15} />
      </button>
      {open && (
        <div className="mt-ms-dropdown">
          {selected.length > 0 && (
            <button type="button" className="ms-clear-all" onClick={() => { onChange([]); setOpen(false); }}>
              Clear all
            </button>
          )}
          {options.map((opt) => (
            <label key={opt.value} className={`ms-option ${selected.includes(opt.value) ? "checked" : ""}`}>
              <span className="ms-checkbox">
                {selected.includes(opt.value) && <CheckIcon size={11} stroke={3} />}
              </span>
              <span className="ms-dot" style={{ background: opt.color || "#94a3b8" }} />
              {opt.label}
              <input type="checkbox" hidden checked={selected.includes(opt.value)} onChange={() => toggle(opt.value)} />
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

/* ────────── Custom Pagination ────────── */
const CustomPagination = ({ currentPage, totalPages, onPageChange, perPage, totalRows, onPerPageChange }) => {
  const pages = useMemo(() => {
    const arr = [];
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    let l;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i < right)) arr.push(i);
    }
    const withEllipsis = [];
    arr.forEach((i) => {
      if (l) {
        if (i - l === 2) withEllipsis.push(l + 1);
        else if (i - l > 2) withEllipsis.push("...");
      }
      withEllipsis.push(i);
      l = i;
    });
    return withEllipsis;
  }, [currentPage, totalPages]);

  const start = totalRows === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalRows);
  const end   = Math.min(currentPage * perPage, totalRows);

  return (
    <div className="mt-pagination">
      <div className="mt-pagination__info">
        <span>Showing <b>{start}</b>–<b>{end}</b> of <b>{totalRows}</b></span>
        <select
          className="mt-pagination__perpage"
          value={perPage}
          onChange={(e) => onPerPageChange(Number(e.target.value))}
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      </div>
      <div className="mt-pagination__pages">
        <button
          className="mt-pagination__btn"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
        >
          <ChevronLeftIcon size={16} />
        </button>
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`e-${i}`} className="mt-pagination__ellipsis">…</span>
          ) : (
            <button
              key={p}
              className={`mt-pagination__btn ${p === currentPage ? "is-active" : ""}`}
              onClick={() => onPageChange(p)}
              aria-current={p === currentPage ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}
        <button
          className="mt-pagination__btn"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
        >
          <ChevronRightIcon size={16} />
        </button>
      </div>
    </div>
  );
};

/* ────────── Main Component ────────── */
const Medicinetype = () => {
  const [loading, setLoading]                         = useState(false);
  const [medicinetype, setMedicinetype]               = useState([]);
  const [filteredMedicinetype, setFilteredMedicinetype] = useState([]);
  const [filterText, setFilterText]                   = useState("");
  const [statusFilter, setStatusFilter]               = useState([]);

  const [name, setName]     = useState("");
  const [status, setStatus] = useState("Active");

  const [messagealert, setMessageAlert] = useState("");
  const [messageType, setMessageType]   = useState("");

  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId]         = useState(null);

  const [selectedRows, setSelectedRows]   = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [bulkDeleting, setBulkDeleting]   = useState(false);
  const [switchingId, setSwitchingId]     = useState(null);

  const [page, setPage]           = useState(1);
  const [perPage, setPerPage]     = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const [showModal, setShowModal] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false, title: "", message: "", confirmLabel: "Confirm",
    confirmVariant: "danger", onConfirm: null,
  });

  const token = localStorage.getItem("authToken");

  const showConfirm = ({ title, message, confirmLabel, confirmVariant = "danger", onConfirm }) => {
    setConfirmDialog({ open: true, title, message, confirmLabel, confirmVariant, onConfirm });
  };
  const closeConfirm = () => setConfirmDialog((p) => ({ ...p, open: false, onConfirm: null }));

  const flashMessage = (msg, type = "success") => {
    setMessageAlert(msg);
    setMessageType(type);
    setTimeout(() => { setMessageAlert(""); setMessageType(""); }, 4000);
  };

  /* ── Fetch Medicine Types ── */
  const fetchMedicinetype = useCallback((currentPage = 1, size = perPage) => {
    setLoading(true);
    fetch(`${API_BASE_URL}api/medicinetype?page=${currentPage}&per_page=${size}`, {
      method: "GET",
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          setMedicinetype(data.data);
          setFilteredMedicinetype(data.data);
          setTotalRows(data.pagination?.total || data.data.length);
          setPage(data.pagination?.current_page || currentPage);
        } else {
          console.error("Unexpected response format:", data);
        }
      })
      .catch((err) => console.error("Error fetching medicinetype:", err))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchMedicinetype(1, perPage); }, []);

  /* ── Pagination handlers ── */
  const handlePageChange = (p) => {
    setPage(p);
    fetchMedicinetype(p, perPage);
  };
  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1);
    fetchMedicinetype(1, newPerPage);
  };
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));

  /* ── Combined filter (text + status multi-select) ── */
  useEffect(() => {
    let result = medicinetype;
    if (filterText) {
      const q = filterText.toLowerCase();
      result = result.filter(
        (item) =>
          item.name?.toLowerCase().includes(q) ||
          String(item.id)?.toLowerCase().includes(q) ||
          item.status?.toLowerCase().includes(q)
      );
    }
    if (statusFilter.length > 0) {
      result = result.filter((item) => statusFilter.includes(item.status));
    }
    setFilteredMedicinetype(result);
  }, [filterText, statusFilter, medicinetype]);

  /* ── Status toggle ── */
  const handleStatusChange = (id, currentStatus) => {
    if (switchingId === id) return;
    const newStatus = currentStatus === "Active" ? "InActive" : "Active";
    showConfirm({
      title: "Change Status",
      message: `Are you sure you want to set this medicine type to "${newStatus}"?`,
      confirmLabel: "Yes, Change",
      confirmVariant: "warning",
      onConfirm: () => {
        closeConfirm();
        setSwitchingId(id);
        fetch(`${API_BASE_URL}api/medicinetype/status/${id}`, {
          method: "GET",
          headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              flashMessage(`Status updated to ${newStatus} successfully!`);
              fetchMedicinetype(page, perPage);
            } else {
              flashMessage("Failed to update status.", "danger");
            }
          })
          .catch(() => flashMessage("Error while updating status.", "danger"))
          .finally(() => setSwitchingId(null));
      },
    });
  };

  /* ── Add or Update ── */
  const handleSaveMedicinetype = (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("name", name);
    formData.append("is_home", 1);
    formData.append("status", status);

    const url = isEditMode
      ? `${API_BASE_URL}api/medicinetype/${editId}`
      : `${API_BASE_URL}api/medicinetype`;

    fetch(url, {
      method: "POST",
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === true) {
          flashMessage(isEditMode ? "Medicine type updated successfully!" : "Medicine type added successfully!");
          setName(""); setStatus("Active"); setIsEditMode(false); setEditId(null);
          setShowModal(false);
          fetchMedicinetype(page, perPage);
        } else {
          flashMessage("Failed to save medicine type.", "danger");
        }
      })
      .catch(() => flashMessage("Something went wrong!", "danger"));
  };

  /* ── Edit ── */
  const handleEdit = (row) => {
    setIsEditMode(true);
    setEditId(row.id);
    setName(row.name);
    setStatus(row.status);
    setShowModal(true);
  };

  /* ── Delete single ── */
  const handleDelete = (id) => {
    showConfirm({
      title: "Delete Medicine Type",
      message: "Are you sure you want to delete this medicine type? This action cannot be undone.",
      confirmLabel: "Yes, Delete",
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        fetch(`${API_BASE_URL}api/medicinetype/destroy/${id}`, {
          method: "GET",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              flashMessage("Medicine type deleted successfully!");
              fetchMedicinetype(page, perPage);
            } else {
              flashMessage("Failed to delete medicine type.", "danger");
            }
          })
          .catch(() => flashMessage("Error deleting medicine type.", "danger"));
      },
    });
  };

  /* ── Bulk delete ── */
  const handleBulkDelete = () => {
    if (selectedRows.length === 0) return;
    const count = selectedRows.length;
    showConfirm({
      title: "Delete Selected",
      message: `Are you sure you want to delete ${count} selected item${count > 1 ? "s" : ""}? This action cannot be undone.`,
      confirmLabel: `Delete ${count} Item${count > 1 ? "s" : ""}`,
      confirmVariant: "danger",
      onConfirm: async () => {
        closeConfirm();
        setBulkDeleting(true);
        try {
          const results = await Promise.all(
            selectedRows.map((row) =>
              fetch(`${API_BASE_URL}api/medicinetype/destroy/${row.id}`, {
                method: "GET",
                headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
              })
                .then((res) => res.json())
                .catch(() => ({ status: false }))
            )
          );
          const successCount = results.filter((r) => r.status).length;
          const failCount = count - successCount;
          if (failCount === 0) {
            flashMessage(`${successCount} item${successCount > 1 ? "s" : ""} deleted successfully!`);
          } else {
            flashMessage(`${successCount} deleted, ${failCount} failed.`, successCount > 0 ? "warning" : "danger");
          }
          setSelectedRows([]);
          setToggleCleared((t) => !t);
          fetchMedicinetype(page, perPage);
        } catch {
          flashMessage("Error during bulk delete.", "danger");
        } finally {
          setBulkDeleting(false);
        }
      },
    });
  };

  /* ── Open Add Modal ── */
  const openAddModal = () => {
    setIsEditMode(false);
    setName("");
    setStatus("Active");
    setEditId(null);
    setShowModal(true);
  };

  /* ── DataTable Custom Styles ── */
  const tableStyles = useMemo(() => ({
    table: { style: { background: "transparent" } },
    headRow: {
      style: {
        background: "linear-gradient(135deg, #f0fbf6 0%, #e8f8f0 100%)",
        borderTopLeftRadius: "0px",
        borderTopRightRadius: "0px",
        borderBottom: "1px solid #d6efe2",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        fontSize: "11.5px",
        fontWeight: 700,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color: "#4f6b5e",
        paddingLeft: "16px",
        paddingRight: "16px",
      },
    },
    rows: {
      style: {
        minHeight: "68px",
        fontSize: "14px",
        color: "#1a2e27",
        borderBottom: "1px solid #eef4f1 !important",
        transition: "background 0.18s ease",
        fontFamily: "'Inter', sans-serif",
      },
      highlightOnHoverStyle: {
        background: "#f6fcf9",
        borderBottomColor: "#e3f0e9",
        outline: "none",
      },
      stripedStyle: { background: "#fafefb" },
    },
    cells: { style: { paddingLeft: "16px", paddingRight: "16px" } },
    pagination: { style: { display: "none" } },
  }), []);

  /* ── Table Columns ── */
  const columns = useMemo(() => [
    {
      name: "ID",
      selector: (row) => row.id,
      cell: (row) => <span className="mt-id-pill">#{row.id}</span>,
      width: "90px",
      sortable: true,
    },
    {
      name: "Name",
      selector: (row) => row.name,
      cell: (row) => (
        <div className="mt-name-cell">
          <span className="mt-name-icon"><PillIcon size={14} /></span>
          <span className="mt-cell-name">{row.name}</span>
        </div>
      ),
      sortable: true,
      grow: 2,
    },
    {
      name: "Status",
      cell: (row) => (
        <div className="mt-status-wrap">
          <IosSwitch
            checked={row.status === "Active"}
            onChange={() => handleStatusChange(row.id, row.status)}
            disabled={switchingId === row.id}
          />
          <span className={`mt-status-label ${row.status === "Active" ? "is-active" : "is-inactive"}`}>
            {row.status === "Active" ? "Active" : "Inactive"}
          </span>
        </div>
      ),
      width: "160px",
    },
    {
      name: "Created At",
      selector: (row) => row.created_at,
      cell: (row) => (
        <span className="mt-cell-muted">
          {new Date(row.created_at).toLocaleDateString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
          })}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="mt-row-actions">
          <button className="mt-ico-btn edit" onClick={() => handleEdit(row)} title="Edit" type="button">
            <EditIcon size={15} />
          </button>
          <button className="mt-ico-btn del" onClick={() => handleDelete(row.id)} title="Delete" type="button">
            <TrashIcon size={15} />
          </button>
        </div>
      ),
      width: "110px",
      ignoreRowClick: true,
      allowOverflow: true,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [switchingId]);

  const statusOptions = [
    { value: "Active",   label: "Active",   color: "#10b981" },
    { value: "InActive", label: "Inactive", color: "#e0413c" },
  ];

  /* ────────── Render ────────── */
  return (
    <>
      <div id="layout-wrapper" className="mt-page">
        <Header />
        {(loading || bulkDeleting) && (
          <div className="mt-overlay"><div className="mt-spinner" /></div>
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">

              {/* ── Page Header ── */}
              <div className="mt-head">
                <div className="mt-head-left">
                  <h2>Medicine Types</h2>
                  <p>Manage and organise your medicine type catalogue.</p>
                </div>
                <button type="button" className="mt-add-btn" onClick={openAddModal}>
                  <PlusIcon size={17} />
                  Add Medicine Type
                </button>
              </div>

              {/* ── Main Card ── */}
              <div className="mt-card">

                {/* Top Bar */}
                <div className="mt-card-top">
                  <div className="mt-search-box">
                    <SearchIcon size={17} />
                    <input
                      type="text"
                      placeholder="Search by name, id or status…"
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                    />
                  </div>
                  <MultiSelect options={statusOptions} selected={statusFilter} onChange={setStatusFilter} />
                  <div className="mt-total-badge">
                    <PillIcon size={15} />
                    Total:&nbsp;<b>{totalRows}</b>
                  </div>
                </div>

                {/* Alert */}
                {messagealert && (
                  <div className={`mt-alert ${messageType}`} role="alert">
                    <InfoIcon size={16} />
                    {messagealert}
                    <button type="button" className="close" onClick={() => setMessageAlert("")}>×</button>
                  </div>
                )}

                {/* Bulk Bar */}
                {selectedRows.length > 0 && (
                  <div className="mt-bulk-bar">
                    <div className="mt-bulk-count">
                      <span className="mt-badge-num">{selectedRows.length}</span>
                      item{selectedRows.length > 1 ? "s" : ""} selected
                    </div>
                    <div className="mt-bulk-actions">
                      <button
                        type="button"
                        className="mt-bulk-btn ghost"
                        onClick={() => { setSelectedRows([]); setToggleCleared((t) => !t); }}
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        className="mt-bulk-btn danger"
                        onClick={handleBulkDelete}
                        disabled={bulkDeleting}
                      >
                        <TrashIcon size={14} />
                        {bulkDeleting ? "Deleting…" : "Delete Selected"}
                      </button>
                    </div>
                  </div>
                )}

                {/* DataTable — built-in pagination hidden; custom pagination below */}
                <DataTable
                  columns={columns}
                  data={filteredMedicinetype}
                  customStyles={tableStyles}
                  selectableRows
                  onSelectedRowsChange={({ selectedRows: rows }) => setSelectedRows(rows)}
                  clearSelectedRows={toggleCleared}
                  highlightOnHover
                  responsive
                  striped
                  pagination={false}
                  noDataComponent={
                    <div style={{ padding: "48px 20px", color: "var(--mt-muted)", textAlign: "center" }}>
                      <PillIcon size={40} />
                      <p style={{ marginTop: 12, fontWeight: 600, fontSize: 15 }}>No medicine types found.</p>
                      <p style={{ fontSize: 13 }}>Try adjusting your search or add a new type.</p>
                    </div>
                  }
                />

                {/* Custom Pagination */}
                {totalRows > 0 && (
                  <CustomPagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    perPage={perPage}
                    totalRows={totalRows}
                    onPerPageChange={handlePerPageChange}
                  />
                )}
              </div>
            </div>
          </div>

          {/* ────────── Add / Edit Modal ────────── */}
          {showModal && (
            <div
              className="mt-modal-overlay"
              onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
              aria-modal="true"
              role="dialog"
              aria-labelledby="mtModalTitle"
            >
              <div className="mt-modal">
                <div className="mt-modal__header">
                  <div className="mt-modal__header-left">
                    <div className="mt-modal__icon">
                      <PillIcon size={20} />
                    </div>
                    <div>
                      <p className="mt-modal__title" id="mtModalTitle">
                        {isEditMode ? "Edit Medicine Type" : "Add New Medicine Type"}
                      </p>
                      <p className="mt-modal__subtitle">
                        {isEditMode ? "Update the details of this type" : "Fill in the details to create a new type"}
                      </p>
                    </div>
                  </div>
                  <button type="button" className="mt-modal__close" onClick={() => setShowModal(false)} aria-label="Close modal">
                    <CloseIcon size={16} />
                  </button>
                </div>

                <form onSubmit={handleSaveMedicinetype}>
                  <div className="mt-modal__body">

                    {/* Name */}
                    <div className="mt-form-group">
                      <label className="mt-form-label" htmlFor="mt-name">Name <span>*</span></label>
                      <input
                        type="text" id="mt-name" className="mt-form-control"
                        placeholder="e.g. Tablet, Capsule, Syrup…"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required autoFocus
                      />
                      <p className="mt-form-hint">Enter a clear, descriptive name for the medicine type.</p>
                    </div>

                    {/* Status */}
                    <div className="mt-form-group">
                      <label className="mt-form-label" htmlFor="mt-status">Status</label>
                      <div className="mt-status-select-wrap">
                        <span
                          className="mt-status-indicator"
                          style={{ background: status === "Active" ? "#10b981" : "#e0413c" }}
                        />
                        <select
                          id="mt-status" className="mt-form-select"
                          value={status} onChange={(e) => setStatus(e.target.value)}
                        >
                          <option value="Active">Active</option>
                          <option value="InActive">Inactive</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="mt-modal__footer">
                    <button type="button" className="mt-btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="mt-btn-submit">
                      {isEditMode ? <><EditIcon size={15} /> Update Medicine Type</> : <><PlusIcon size={15} /> Add Medicine Type</>}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Custom Confirm Dialog */}
          <ConfirmDialog
            open={confirmDialog.open}
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmLabel={confirmDialog.confirmLabel}
            confirmVariant={confirmDialog.confirmVariant}
            onConfirm={confirmDialog.onConfirm}
            onCancel={closeConfirm}
          />

          <Footer />
        </div>
      </div>
    </>
  );
};

export default Medicinetype;